/**
 * Hooks for project membership and privilege checks.
 *
 *   useProjectMembership(projectId)   — current user's member doc + has(priv)
 *   useProjectMembers(projectId)      — full list of members (for Team panel)
 *   useProjectInvitations(projectId)  — pending invitations (for Team panel)
 *   useProjectAuditLog(projectId)     — change-history feed (Activity tab)
 */

import { useCallback, useEffect, useState } from 'react';
import { onSnapshot, query, orderBy, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  invitationsCollection,
  memberDoc,
  membersCollection,
} from '../constants/firestorePaths';
import {
  AccessRole,
  Privilege,
  ProjectMemberDoc,
  memberHasPrivilege,
} from '../lib/permissions';
import { CLOUD_FUNCTIONS } from '../constants/firebase';
import { auditTimestampToDate, mergeAuditPages } from '../lib/auditExport';

export interface ProjectMembership {
  member: ProjectMemberDoc | null;
  role: AccessRole | null;
  loading: boolean;
  has: (privilege: Privilege) => boolean;
}

export function useProjectMembership(projectId: string | undefined): ProjectMembership {
  const { user } = useAuth();
  const [member, setMember] = useState<ProjectMemberDoc | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId || !user) {
      setMember(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      memberDoc(projectId, user.uid),
      snap => {
        setMember(snap.exists() ? (snap.data() as ProjectMemberDoc) : null);
        setLoading(false);
      },
      err => {
        console.warn('useProjectMembership snapshot error:', err);
        setMember(null);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [projectId, user?.uid]);

  return {
    member,
    role: member?.accessRole ?? null,
    loading,
    has: (privilege: Privilege) =>
      member ? memberHasPrivilege(member, privilege) : false,
  };
}

export interface ProjectMembersState {
  members: ProjectMemberDoc[];
  loading: boolean;
}

export function useProjectMembers(projectId: string | undefined): ProjectMembersState {
  const [members, setMembers] = useState<ProjectMemberDoc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) {
      setMembers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      membersCollection(projectId),
      snap => {
        const docs = snap.docs.map(d => d.data() as ProjectMemberDoc);
        // Owner first, then the rest by display name / email.
        docs.sort((a, b) => {
          if (a.accessRole === 'owner' && b.accessRole !== 'owner') return -1;
          if (b.accessRole === 'owner' && a.accessRole !== 'owner') return 1;
          const aName = a.displayName || a.email || a.userId;
          const bName = b.displayName || b.email || b.userId;
          return aName.localeCompare(bName);
        });
        setMembers(docs);
        setLoading(false);
      },
      err => {
        console.warn('useProjectMembers snapshot error:', err);
        setMembers([]);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [projectId]);

  return { members, loading };
}

export interface ProjectInvitation {
  token: string;
  invitedEmail: string;
  accessRole: AccessRole;
  invitedByName?: string;
  invitedAt?: { toDate?: () => Date };
  expiresAt?: { toDate?: () => Date };
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
}

export interface ProjectInvitationsState {
  invitations: ProjectInvitation[];
  loading: boolean;
}

export function useProjectInvitations(
  projectId: string | undefined
): ProjectInvitationsState {
  const [invitations, setInvitations] = useState<ProjectInvitation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) {
      setInvitations([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(
      invitationsCollection(projectId),
      where('status', '==', 'pending'),
      orderBy('invitedAt', 'desc')
    );
    const unsub = onSnapshot(
      q,
      snap => {
        const docs = snap.docs.map(d => ({
          token: d.id,
          ...(d.data() as Omit<ProjectInvitation, 'token'>),
        }));
        setInvitations(docs);
        setLoading(false);
      },
      err => {
        console.warn('useProjectInvitations snapshot error:', err);
        setInvitations([]);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [projectId]);

  return { invitations, loading };
}

// ── Audit log ────────────────────────────────────────────────────────

export type AuditAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'approve'
  | 'reject'
  | 'submit_for_review'
  | 'view'
  | 'export'
  | 'agent_action'
  | 'issue';

export interface AuditLogEntry {
  id: string;
  action: AuditAction;
  resourceType?: string;
  resourceId?: string;
  userId: string;
  userEmail?: string;
  userName?: string;
  description: string;
  timestamp?: { toDate?: () => Date };
  previousValue?: Record<string, unknown>;
  newValue?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface ProjectAuditLogState {
  entries: AuditLogEntry[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
}

const AUDIT_PAGE_SIZE = 50;

/**
 * Paginated activity-log feed for a project, newest first.
 *
 * Reads through the `getAuditLogsV2` callable (which enforces the
 * `view_activity` privilege server-side) rather than hitting Firestore
 * directly. The first page loads on mount; `loadMore()` pages further
 * back using the oldest loaded entry's timestamp as an (inclusive)
 * `endDate` cursor. The cursor's boundary entry comes back on the next
 * page, so `mergeAuditPages` de-duplicates by id while preserving order.
 */
export function useProjectAuditLog(
  projectId: string | undefined,
  pageSize = AUDIT_PAGE_SIZE
): ProjectAuditLogState {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const fetchPage = useCallback(
    async (endDate?: string): Promise<AuditLogEntry[]> => {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.GET_AUDIT_LOGS);
      const res = await fn({ projectId, limit: pageSize, endDate });
      return (res.data as AuditLogEntry[]) ?? [];
    },
    [projectId, pageSize]
  );

  useEffect(() => {
    if (!projectId) {
      setEntries([]);
      setLoading(false);
      setHasMore(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEntries([]);
    setHasMore(false);
    fetchPage()
      .then(page => {
        if (cancelled) return;
        setEntries(page);
        setHasMore(page.length === pageSize);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.warn('useProjectAuditLog fetch error:', err);
        setError(err?.message || 'Could not load activity.');
        setEntries([]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, pageSize, fetchPage]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore || entries.length === 0) return;
    setLoadingMore(true);
    setError(null);
    const oldest = entries[entries.length - 1];
    const cursor = auditTimestampToDate(oldest.timestamp);
    fetchPage(cursor ? cursor.toISOString() : undefined)
      .then(page => {
        setEntries(prev => mergeAuditPages(prev, page));
        setHasMore(page.length === pageSize);
      })
      .catch(err => {
        console.warn('useProjectAuditLog loadMore error:', err);
        setError(err?.message || 'Could not load more activity.');
      })
      .finally(() => setLoadingMore(false));
  }, [entries, hasMore, loadingMore, pageSize, fetchPage]);

  return { entries, loading, loadingMore, error, hasMore, loadMore };
}
